/**
 * Lookup Data Configurations
 *
 * Pre-configured FetchConfig objects for all lookup types
 * Makes it easy to integrate lookups into editor components
 *
 * Usage in components:
 * const lookupConfig = lookupConfigs.instructionType;
 * <LookupSelect fetchConfig={lookupConfig} value={typeId} onChange={setTypeId} />
 */

import { FetchConfig } from './hooks/useLazyLookupData';
import { apiConfig, authHeaders } from '@flow-studio/api';
import { createGuid } from '@bizfirst/common-js';

/**
 * Headers for lookup requests: tenant + Bearer token (same as BaseApiClient.getHeaders)
 */
const lookupHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Tenant-ID': String(apiConfig.tenantId),
  };
  try {
    const token = localStorage.getItem('authToken');
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch {
    // localStorage unavailable
  }
  return headers;
};

/**
 * Generic API client wrapper for getById
 * Handles both direct responses and wrapped responses
 */
const createGetById = (
  endpoint: string,
  _idParamName: string = 'ID'
): ((id: number) => Promise<any>) => {
  return async (id: number) => {
    const response = await fetch(`${apiConfig.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: lookupHeaders(),
      body: JSON.stringify({
        metadata: {
          requestId: createGuid(),
          correlationId: createGuid(),
          timestamp: new Date().toISOString(),
          version: '1.0',
        },
        [_idParamName]: {
          [_idParamName]: id,   //  Nested structure: { "ID": { "ID": 123 } }
        },

      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || data;
  };
};

/**
 * Generic API client wrapper for getById where the ID is a URL path
 * parameter (e.g. POST /api/v1/documents/collections/{id}) rather than
 * createGetById()'s convention of a fixed "/by-id" (or "/get-by-id") suffix
 * route with the ID passed only in the body.
 */
const createGetByIdWithPathParam = (
  endpointBase: string,
  _idParamName: string = 'ID'
): ((id: number) => Promise<any>) => {
  return async (id: number) => {
    const response = await fetch(`${apiConfig.baseUrl}${endpointBase}/${id}`, {
      method: 'POST',
      headers: lookupHeaders(),
      body: JSON.stringify({
        metadata: {
          requestId: createGuid(),
          correlationId: createGuid(),
          timestamp: new Date().toISOString(),
          version: '1.0',
        },
        [_idParamName]: {
          [_idParamName]: id,   //  Nested structure: { "ID": { "ID": 123 } }
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || data;
  };
};

/**
 * Generic API client wrapper for getAll with pagination
 */
const createGetAll = (endpoint: string): ((request?: any) => Promise<any>) => {
  return async (request?: any) => {
    const response = await fetch(`${apiConfig.baseUrl}${endpoint}/list`, {
      method: 'POST',
      headers: lookupHeaders(),
      body: JSON.stringify({
        metadata: {
          requestId: createGuid(),
          correlationId: createGuid(),
          timestamp: new Date().toISOString(),
          version: '1.0',
        },
        ...request,
      }),
    });
    if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);
    const data = await response.json();
    return data.data || data;
  };
};


/**
 * Entity-specific transform functions
 * Maps entity-specific field names to the standard 'name' property
 */

const instructionTypeTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.InstructionName || item.instructionName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const channelTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.ChannelName || item.channelName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const profileTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.ProfileName || item.profileName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const agentTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.AgentName || item.agentName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const mcpToolTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.ToolName || item.toolName || item.McpToolName || item.mcpToolName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const functionTypeTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.FunctionTypeName || item.functionTypeName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const documentCollectionTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.DocumentCollectionID || item.documentCollectionID || item.id || item.ID,
      name: item.CollectionName || item.collectionName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const connectorGroupTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.ConnectorGroupID || item.connectorGroupID || item.id || item.ID,
      name: item.GroupName || item.groupName || item.ConnectorGroupName || item.connectorGroupName || item.Name || item.name,
      ...item,
    }));
  }
  return [];
};

const connectorTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.ConnectorID || item.connectorID || item.id || item.ID,
      name: item.ConnectorName || item.connectorName || item.Name || item.name,
      ...item,
    }));
  }
  return [];
};

const ragProviderTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.ProviderName || item.providerName || item.EmbeddingProviderName || item.embeddingProviderName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const embeddingModelTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.ModelName || item.modelName || item.EmbeddingModelName || item.embeddingModelName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const businessDomainTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.DomainName || item.domainName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const businessCategoryTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.CategoryName || item.categoryName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const businessClassificationTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.ClassificationName || item.classificationName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const businessSectorTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.SectorName || item.sectorName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const businessIndustryTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.IndustryName || item.industryName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const businessProfileTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.ProfileName || item.profileName || item.BusinessProfileName || item.businessProfileName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const llmProviderTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.ProviderName || item.providerName || item.LlmProviderName || item.llmProviderName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const modelCategoryTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.CategoryName || item.categoryName || item.ModelCategoryName || item.modelCategoryName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const modelTypeTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.TypeName || item.typeName || item.ModelTypeName || item.modelTypeName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const hookTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.HookName || item.hookName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const identityTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.IdentityName || item.identityName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const roleTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.RoleName || item.roleName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const userTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.UserName || item.userName || item.Email || item.email || `${item.FirstName || ''} ${item.LastName || ''}`.trim() || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const teamTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.TeamName || item.teamName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const agentTypeTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.AgentTypeID || item.agentTypeID || item.id || item.ID,
      name: item.TypeName || item.typeName || item.AgentTypeName || item.agentTypeName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const agentCategoryTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.AgentCategoryID || item.agentCategoryID || item.id || item.ID,
      name: item.Name || item.name || item.AgentCategoryName || item.agentCategoryName,
      ...item,
    }));
  }
  return [];
};

const agentClassificationTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.AgentClassificationID || item.agentClassificationID || item.id || item.ID,
      name: item.Name || item.name || item.AgentClassificationName || item.agentClassificationName,
      ...item,
    }));
  }
  return [];
};

const llmModelTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.id || item.ID,
      name: item.ModelName || item.modelName || item.LlmModelName || item.llmModelName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

const performanceIntensityTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.PerformanceOptimizationIntensityID || item.performanceOptimizationIntensityID || item.id || item.ID,
      name: item.IntensityName || item.intensityName || item.Name || item.name,
      ...item,
    }));
  }
  return [];
};

const qualityIntensityTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.ResponseQualityIntensityID || item.responseQualityIntensityID || item.id || item.ID,
      name: item.IntensityName || item.intensityName || item.Name || item.name,
      ...item,
    }));
  }
  return [];
};

const channelGroupTransform = (response: any) => {
  if (Array.isArray(response)) {
    return response.map(item => ({
      id: item.channelGroupID || item.ID,
      name: item.groupName || item.channelGroupName || item.name || item.Name,
      ...item,
    }));
  }
  return [];
};

/**
 * Pre-configured lookup configurations
 */
export const lookupConfigs: Record<string, FetchConfig> = {
  // ============================================
  // Instruction-Related Lookups
  // ============================================

  /**
   * Instruction Type - Required field in InstructionEditor
   */
  instructionType: {
    cacheKey: 'lookup_instructionType',
    getById: createGetById('/api/v1/ai-agent/instruction-types/by-id'),
    getAll: createGetAll('/api/v1/ai-agent/instruction-types'),
    transformData: instructionTypeTransform,
  },

  /**
   * Channel - Optional field in InstructionEditor
   */
  channel: {
    cacheKey: 'lookup_channel',
    getById: createGetById('/api/v1/ai-channel/channels/by-id'),
    getAll: createGetAll('/api/v1/ai-channel/channels'),
    transformData: channelTransform,
  },

  /**
   * Profile - Optional field in InstructionEditor
   */
  profile: {
    cacheKey: 'lookup_profile',
    getById: createGetById('/api/v1/ai-agent/profiles/by-id'),
    getAll: createGetAll('/api/v1/ai-agent/profiles'),
    transformData: profileTransform,
  },

  // ============================================
  // AI Function-Related Lookups
  // ============================================

  /**
   * Agent - Required parent reference in AIFunctionEditor
   */
  agent: {
    cacheKey: 'lookup_agent',
    getById: createGetById('/api/v1/ai-agent/agents/get-by-id'),
    getAll: createGetAll('/api/v1/ai-agent/agents'),
    transformData: agentTransform,
  },

  /**
   * MCP Tool - Optional field in AIFunctionEditor
   */
  mcpTool: {
    cacheKey: 'lookup_mcpTool',
    getById: createGetById('/api/v1/ai-mcp/tools/by-id'),
    getAll: createGetAll('/api/v1/ai-mcp/tools'),
    transformData: mcpToolTransform,
  },

  /**
   * Function Type - Optional field in AIFunctionEditor
   */
  functionType: {
    cacheKey: 'lookup_functionType',
    getById: createGetById('/api/v1/ai-function/function-types/by-id'),
    getAll: createGetAll('/api/v1/ai-function/function-types'),
    transformData: functionTypeTransform,
  },

  /**
   * Connector Group - Optional field in AIFunctionEditor
   */
  connectorGroup: {
    cacheKey: 'lookup_connectorGroup',
    getById: createGetById('/api/v1/ai-extension/connector-groups/get-by-id'),
    getAll: createGetAll('/api/v1/ai-extension/connector-groups'),
    transformData: connectorGroupTransform,
  },

  /**
   * Document Collection - Optional field in KnowledgeBaseEditor (AgentFunctionKnowledgeBase)
   * Note: getById uses the ID-in-URL-path shape (POST /api/v1/documents/collections/{id}),
   * not the createGetById() fixed "/by-id" suffix convention used by other lookups in this file.
   */
  documentCollection: {
    cacheKey: 'lookup_documentCollection',
    getById: createGetByIdWithPathParam('/api/v1/documents/collections'),
    getAll: createGetAll('/api/v1/documents/collections'),
    transformData: documentCollectionTransform,
  },

  // ============================================
  // Channel-Related Lookups
  // ============================================

  /**
   * Connector - Optional field in ChannelEditor
   */
  connector: {
    cacheKey: 'lookup_connector',
    getById: createGetById('/api/v1/ai-extension/connectors/get-by-id'),
    getAll: createGetAll('/api/v1/ai-extension/connectors'),
    transformData: connectorTransform,
  },

  // ============================================
  // Knowledge Base-Related Lookups
  // ============================================

  /**
   * RAG Provider / Embedding Provider - Optional field in KnowledgeBaseEditor
   */
  ragProvider: {
    cacheKey: 'lookup_ragProvider',
    getById: createGetById('/api/v1/ai/embedding-providers/by-id'),
    getAll: createGetAll('/api/v1/ai/embedding-providers'),
    transformData: ragProviderTransform,
  },

  /**
   * Embedding Model - Optional field in KnowledgeBaseEditor
   */
  embeddingModel: {
    cacheKey: 'lookup_embeddingModel',
    getById: createGetById('/api/v1/ai/embedding-models/by-id'),
    getAll: createGetAll('/api/v1/ai/embedding-models'),
    transformData: embeddingModelTransform,
  },

  /**
   * Business Domain - Optional taxonomy field in KnowledgeBaseEditor
   */
  businessDomain: {
    cacheKey: 'lookup_businessDomain',
    getById: createGetById('/api/v1/taxonomy/business-domain/by-id'),
    getAll: createGetAll('/api/v1/taxonomy/business-domain'),
    transformData: businessDomainTransform,
  },

  /**
   * Business Category - Optional taxonomy field in KnowledgeBaseEditor
   */
  businessCategory: {
    cacheKey: 'lookup_businessCategory',
    getById: createGetById('/api/v1/ai-taxonomy/business-categories/by-id'),
    getAll: createGetAll('/api/v1/ai-taxonomy/business-categories'),
    transformData: businessCategoryTransform,
  },

  /**
   * Business Classification - Optional taxonomy field in KnowledgeBaseEditor
   */
  businessClassification: {
    cacheKey: 'lookup_businessClassification',
    getById: createGetById('/api/v1/ai-taxonomy/business-classifications/by-id'),
    getAll: createGetAll('/api/v1/ai-taxonomy/business-classifications'),
    transformData: businessClassificationTransform,
  },

  /**
   * Business Sector - Optional taxonomy field in KnowledgeBaseEditor
   */
  businessSector: {
    cacheKey: 'lookup_businessSector',
    getById: createGetById('/api/v1/taxonomy/business-sector/by-id'),
    getAll: createGetAll('/api/v1/taxonomy/business-sector'),
    transformData: businessSectorTransform,
  },

  /**
   * Business Industry - Optional taxonomy field in KnowledgeBaseEditor
   */
  businessIndustry: {
    cacheKey: 'lookup_businessIndustry',
    getById: createGetById('/api/v1/taxonomy/business-industry/by-id'),
    getAll: createGetAll('/api/v1/taxonomy/business-industry'),
    transformData: businessIndustryTransform,
  },

  /**
   * Business Profile - Optional taxonomy field in KnowledgeBaseEditor
   */
  businessProfile: {
    cacheKey: 'lookup_businessProfile',
    getById: createGetById('/api/v1/taxonomy/business-profile/by-id'),
    getAll: createGetAll('/api/v1/taxonomy/business-profile'),
    transformData: businessProfileTransform,
  },

  // ============================================
  // LLM Model-Related Lookups
  // ============================================

  /**
   * LLM Provider - Optional field in LLMModelEditor
   */
  llmProvider: {
    cacheKey: 'lookup_llmProvider',
    getById: createGetById('/api/v1/ai-llm/providers/by-id'),
    getAll: createGetAll('/api/v1/ai-llm/providers'),
    transformData: llmProviderTransform,
  },

  /**
   * Model Category - Optional field in LLMModelEditor
   */
  modelCategory: {
    cacheKey: 'lookup_modelCategory',
    getById: createGetById('/api/v1/ai-common/model-categories/by-id'),
    getAll: createGetAll('/api/v1/ai-common/model-categories'),
    transformData: modelCategoryTransform,
  },

  /**
   * Model Type - Optional field in LLMModelEditor
   */
  modelType: {
    cacheKey: 'lookup_modelType',
    getById: createGetById('/api/v1/ai-common/model-types/by-id'),
    getAll: createGetAll('/api/v1/ai-common/model-types'),
    transformData: modelTypeTransform,
  },

  // ============================================
  // Assignment-Related Lookups
  // ============================================

  /**
   * Hook - for AssignmentModal
   */
  hook: {
    cacheKey: 'lookup_hook',
    getById: createGetById('/api/v1/ai-extension/hooks/get-by-id'),
    getAll: createGetAll('/api/v1/ai-extension/hooks'),
    transformData: hookTransform,
  },

  /**
   * Identity - for AssignmentModal
   */
  identity: {
    cacheKey: 'lookup_identity',
    getById: createGetById('/api/v1/ai-agent/identities/by-id'),
    getAll: createGetAll('/api/v1/ai-agent/identities'),
    transformData: identityTransform,
  },

  /**
   * Role - for AssignmentModal
   */
  role: {
    cacheKey: 'lookup_role',
    getById: createGetById('/api/v1/ai-agent/roles/by-id'),
    getAll: createGetAll('/api/v1/ai-agent/roles'),
    transformData: roleTransform,
  },

  /**
   * User - for AssignmentModal
   */
  user: {
    cacheKey: 'lookup_user',
    getById: createGetById('/api/v1/iam/users/by-id'),
    getAll: createGetAll('/api/v1/iam/users'),
    transformData: userTransform,
  },

  /**
   * Team - for AssignmentModal
   */
  team: {
    cacheKey: 'lookup_team',
    getById: createGetById('/api/v1/ai-agent/teams/by-id'),
    getAll: createGetAll('/api/v1/ai-agent/teams'),
    transformData: teamTransform,
  },

  // ============================================
  // Agent-Related Lookups (Additional)
  // ============================================

  /**
   * Agent Type - for AgentEditor
   */
  agentType: {
    cacheKey: 'lookup_agentType',
    getById: createGetById('/api/v1/ai-agent/agent-types/by-id'),
    getAll: createGetAll('/api/v1/ai-agent/agent-types'),
    transformData: agentTypeTransform,
  },

  /**
   * Agent Category - for AgentEditor
   */
  agentCategory: {
    cacheKey: 'lookup_agentCategory',
    getById: createGetById('/api/v1/ai-common/agent-categories/by-id'),
    getAll: createGetAll('/api/v1/ai-common/agent-categories'),
    transformData: agentCategoryTransform,
  },

  /**
   * Agent Classification - for AgentEditor
   */
  agentClassification: {
    cacheKey: 'lookup_agentClassification',
    getById: createGetById('/api/v1/ai-common/agent-classifications/by-id'),
    getAll: createGetAll('/api/v1/ai-common/agent-classifications'),
    transformData: agentClassificationTransform,
  },

  /**
   * LLM Model - for AgentEditor
   */
  llmModel: {
    cacheKey: 'lookup_llmModel',
    getById: createGetById('/api/v1/ai-common/llm-models/by-id'),
    getAll: createGetAll('/api/v1/ai-common/llm-models'),
    transformData: llmModelTransform,
  },

  /**
   * Performance Optimization Intensity - for AgentEditor
   */
  performanceIntensity: {
    cacheKey: 'lookup_performanceIntensity',
    getById: createGetById('/api/v1/ai-common/performance-optimization-intensities/by-id'),
    getAll: createGetAll('/api/v1/ai-common/performance-optimization-intensities'),
    transformData: performanceIntensityTransform,
  },

  /**
   * Response Quality Intensity - for AgentEditor
   */
  qualityIntensity: {
    cacheKey: 'lookup_qualityIntensity',
    getById: createGetById('/api/v1/ai-common/response-quality-intensities/by-id'),
    getAll: createGetAll('/api/v1/ai-common/response-quality-intensities'),
    transformData: qualityIntensityTransform,
  },

  /**
   * Channel Group - for AgentEditor
   */
  channelGroup: {
    cacheKey: 'lookup_channelGroup',
    getById: createGetById('/api/v1/ai-channel/channel-groups/by-id'),
    getAll: createGetAll('/api/v1/ai-channel/channel-groups'),
    transformData: channelGroupTransform,
  },

  /**
   * Profile - for AssignmentModal
   */
  agentProfile: {
    cacheKey: 'lookup_agentProfile',
    getById: createGetById('/api/v1/ai-agent/profiles/by-id'),
    getAll: createGetAll('/api/v1/ai-agent/profiles'),
    transformData: profileTransform,
  },
};

/**
 * Get lookup configuration by key
 */
export const getLookupConfig = (key: string): FetchConfig | null => {
  return lookupConfigs[key] || null;
};

/**
 * Register custom lookup configuration
 */
export const registerLookupConfig = (key: string, config: FetchConfig): void => {
  lookupConfigs[key] = config;
};

export default lookupConfigs;
